import {
  readBarStructure,
  readBarVerdict,
  readBarView,
  readLogNumber,
  type BarBarrister,
  type BarReviewVerdict,
  type BarStructure,
  type BarUnattributedHighlight,
} from "./barReview.js";
import styles from "./ParsingJobs.module.css";

// The headline counters, in reading order. A template that never emitted one of them leaves the
// value null and the tile is dropped, so a non-UK Bar document shows a short row, not blanks.
const KPI_TILES: { key: string; label: string }[] = [
  { key: "barristers_unique", label: "Barristers" },
  { key: "barrister_boxes", label: "Nominee boxes" },
  { key: "work_highlights_total", label: "Work highlights" },
  { key: "work_highlights_unattributed", label: "Unattributed" },
  { key: "work_highlights_uncertain", label: "Uncertain" },
  { key: "bar_contacts_tables", label: "Contacts tables" },
];

// The banner says "Needs review" at both levels, exactly as the list chip does; this sentence is
// the only place the difference between them is put into words. It belongs here and not on the
// chip because the banner has the room for a sentence and a table cell does not.
const VERDICT_BLURB: Record<BarReviewVerdict["tier"], string> = {
  major: "something the document contains is missing from the parse output.",
  minor: "the output is there, but an identity field may be wrong.",
};

// One hue, sized against the busiest barrister in this document. The bar carries magnitude only;
// the count beside it is the label, in a text token, so the colour never has to be read.
const HIGHLIGHT_TRACK_REM = 8;
const HIGHLIGHT_MIN_REM = 0.125;

function VerdictBanner({ verdict }: { verdict: BarReviewVerdict }) {
  const isMajor = verdict.tier === "major";
  return (
    <div className={styles.barVerdict}>
      <p className={styles.barVerdictHeadline}>
        <span
          className={`${styles.reviewMarker} ${isMajor ? "" : styles.reviewMarkerHollow}`}
          aria-hidden="true"
        />
        <span className={styles.barVerdictTier}>Needs review</span>
        <span className={styles.barVerdictBlurb}>{VERDICT_BLURB[verdict.tier]}</span>
      </p>
      {verdict.reasons.length === 0 ? (
        <p className={styles.barNote}>No reason details recorded.</p>
      ) : (
        <ul className={styles.barVerdictReasons}>
          {verdict.reasons.map(reason => (
            <li key={reason.code}>
              <span>{reason.label}</span>
              <span className={styles.barVerdictReasonCount}>{reason.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KpiRow({ log }: { log: Record<string, unknown> | null }) {
  const tiles = KPI_TILES.map(tile => ({ ...tile, value: readLogNumber(log, tile.key) })).filter(
    (tile): tile is { key: string; label: string; value: number } => tile.value !== null,
  );
  if (tiles.length === 0) return null;

  return (
    <dl className={styles.barKpiRow}>
      {tiles.map(tile => (
        <div key={tile.key} className={styles.barKpiTile}>
          <dt className={styles.barKpiLabel}>{tile.label}</dt>
          <dd className={styles.barKpiValue}>{tile.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function HighlightMeter({ count, max }: { count: number; max: number }) {
  const width = max > 0 ? Math.max(HIGHLIGHT_MIN_REM, (count / max) * HIGHLIGHT_TRACK_REM) : 0;
  return (
    <div className={styles.barHighlightMeter}>
      {count > 0 && (
        <div
          className={styles.barHighlightBar}
          style={{ width: `${width}rem` }}
          aria-hidden="true"
        />
      )}
      <span className={styles.barHighlightValue}>{count}</span>
    </div>
  );
}

function BarristersTable({ barristers }: { barristers: BarBarrister[] }) {
  const max = barristers.reduce((peak, entry) => Math.max(peak, entry.workHighlights), 0);

  if (barristers.length === 0) {
    return (
      <section className={styles.barSection}>
        <h4 className={styles.barSectionTitle}>Barristers</h4>
        <p className={styles.barNote}>No nominee boxes were read from this document.</p>
      </section>
    );
  }

  return (
    <section className={styles.barSection}>
      <h4 className={styles.barSectionTitle}>Barristers</h4>
      <p className={styles.barNote}>
        In document order, so the rows follow the nominee boxes in the .docx.
      </p>
      <div className={styles.barTableScroll}>
        <table className={styles.barTable}>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Year of call</th>
              <th scope="col">Silk year</th>
              <th scope="col">Highlights</th>
              <th scope="col">Uncertain</th>
              <th scope="col">Boxes</th>
              <th scope="col">Doc position</th>
            </tr>
          </thead>
          <tbody>
            {barristers.map((barrister, index) => (
              <tr key={`${barrister.docPosition}-${barrister.name}-${index}`}>
                <td>{barrister.name || <span className={styles.barMuted}>(no name)</span>}</td>
                <td className={styles.barNumeric}>{barrister.callYear || "—"}</td>
                <td className={styles.barNumeric}>{barrister.silkYear || "—"}</td>
                <td>
                  <HighlightMeter count={barrister.workHighlights} max={max} />
                </td>
                <td className={styles.barNumeric}>{barrister.workHighlightsUncertain ?? "—"}</td>
                <td className={styles.barNumeric}>{barrister.boxes}</td>
                <td className={styles.barMuted}>{barrister.docPosition || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UnattributedTable({ highlights }: { highlights: BarUnattributedHighlight[] }) {
  return (
    <section className={styles.barSection}>
      <h4 className={styles.barSectionTitle}>Unattributed highlights</h4>
      {highlights.length === 0 ? (
        <p className={styles.barNote}>Every work highlight found its nominee.</p>
      ) : (
        <div className={styles.barTableScroll}>
          <table className={styles.barTable}>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Highlights</th>
                <th scope="col">Assigned to</th>
                <th scope="col">Uncertain</th>
                <th scope="col">Doc positions</th>
              </tr>
            </thead>
            <tbody>
              {highlights.map((highlight, index) => (
                <tr key={`${highlight.name}-${index}`}>
                  <td>{highlight.name || <span className={styles.barMuted}>(no name)</span>}</td>
                  <td className={styles.barNumeric}>{highlight.workHighlights}</td>
                  <td>{highlight.assignedTo || <span className={styles.barMuted}>nobody</span>}</td>
                  <td>{highlight.uncertain ? "Yes" : "No"}</td>
                  <td className={styles.barMuted}>{highlight.docPositions.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StructureSection({ structure }: { structure: BarStructure }) {
  return (
    <section className={styles.barSection}>
      <h4 className={styles.barSectionTitle}>Structure measurement</h4>
      <p className={styles.barNote}>
        How far this document's tables drift from the UK Bar 2027 template skeleton. Zero is a clean
        match.
      </p>

      {structure.measured.length > 0 && (
        <dl className={styles.barMeasuredList}>
          {structure.measured.map(entry => (
            <div key={entry.key} className={styles.barMeasuredItem}>
              <dt>{entry.label}</dt>
              <dd className={styles.barNumeric}>{entry.count}</dd>
            </div>
          ))}
        </dl>
      )}

      {structure.deviations.length > 0 && (
        <ul className={styles.barDeviationList}>
          {structure.deviations.map(entry => (
            <li
              key={entry.key}
              className={entry.count > 0 ? styles.barDeviationHit : styles.barDeviationClean}
            >
              <span>{entry.label}</span>
              <span className={styles.barNumeric}>{entry.count}</span>
            </li>
          ))}
        </ul>
      )}

      {structure.missingLabels.length > 0 && (
        <>
          <h5 className={styles.barSubTitle}>Missing labels</h5>
          <ul className={styles.barDeviationList}>
            {structure.missingLabels.map(entry => (
              <li key={entry.key} className={styles.barDeviationHit}>
                <span>{entry.label}</span>
                <span className={styles.barNumeric}>{entry.count}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * The parser's log, made readable. Rendered full width under the detail page's two columns because
 * the barrister table is far wider than the right-hand column.
 *
 * Every parse has a log, so the raw-log section always renders; the Bar sections above it only
 * appear for a document the PHP Bar gate matched.
 */
function BarSummaryPanel({ parsedJson }: { parsedJson: unknown }) {
  const view = readBarView(parsedJson);
  if (!view) return null;

  const verdict = readBarVerdict(view.log);
  const structure = readBarStructure(view.log);

  return (
    <div className={styles.barPanel}>
      <h3 className={styles.jsonTitle}>Parser output</h3>

      {view.hasBar && (
        <>
          {verdict && <VerdictBanner verdict={verdict} />}
          <KpiRow log={view.log} />
          <BarristersTable barristers={view.barristers} />
          {view.unattributedHighlights && (
            <UnattributedTable highlights={view.unattributedHighlights} />
          )}
          {structure && <StructureSection structure={structure} />}
        </>
      )}

      {view.log && (
        <details className={styles.barRawLog}>
          <summary className={styles.barRawLogSummary}>Raw parser log</summary>
          <pre className={styles.barRawLogPre}>{JSON.stringify(view.log, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

export default BarSummaryPanel;
